import React, {useLayoutEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {ArrowLeft, CheckCircle2, KeyRound} from 'lucide-react';
import {membersEmailApi} from '../../services/membersEmailApi.ts';
import {membersError} from '../../services/membersApi.ts';
import {Card, Field, Loading, MembersShell, Notice, primary} from './MembersShared.tsx';
import MembersAsaasSeal from './MembersAsaasSeal.tsx';

export function MembersForgotPassword() {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get('email')).trim();
    setBusy(true); setError('');
    try {await membersEmailApi.requestReset(email); setSent(true);}
    catch(err) {setError(membersError(err));} finally {setBusy(false);}
  }
  return (
    <MembersShell>
      <Card className="mx-auto max-w-lg">
        <KeyRound className="members-link mb-4" size={28} aria-hidden="true" />
        <h1 className="text-2xl font-semibold">Recuperar acesso</h1>
        <p className="members-secondary my-4 text-sm leading-relaxed">Informe o e-mail da sua conta de parceiro Sminy Members para solicitar um link de redefinição de senha.</p>
        {sent ? <Notice>Se houver uma conta associada a esse e-mail, você receberá as instruções para redefinir a senha. Confira também a pasta de spam.</Notice> : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="E-mail" name="email" type="email" autoComplete="email" maxLength={254} required disabled={busy} />
            {error && <Notice error>{error}</Notice>}
            <button className={`${primary} w-full`} disabled={busy}>{busy ? 'Enviando solicitação...' : 'Solicitar link de recuperação'}</button>
          </form>
        )}
        <Link className="members-link mt-6 inline-flex min-h-[40px] items-center gap-2 text-sm" to="/members/login"><ArrowLeft size={16} aria-hidden="true" />Voltar para o login</Link>
        <MembersAsaasSeal />
      </Card>
    </MembersShell>
  );
}
export function MembersResetPassword() {
  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState('');
  const captured = useRef(false);
  useLayoutEffect(() => {
    // Guard preserves the captured token during StrictMode's effect replay.
    if (captured.current) return;
    captured.current = true;
    const value = new URLSearchParams(window.location.hash.slice(1)).get('token');
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    if (value && /^[A-Za-z0-9_-]{32,200}$/.test(value)) setToken(value);
    else setInvalid(true);
    setReady(true);
  }, []);
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const password = String(data.get('password'));
    if (password.length < 12 || new Blob([password]).size > 72) {setError('Use pelo menos 12 caracteres. Se a senha for muito longa, reduza seu tamanho.'); return;}
    if (password !== String(data.get('confirmation'))) {setError('As senhas precisam ser iguais. Confira e tente novamente.'); return;}
    setBusy(true); setError('');
    try {
      await membersEmailApi.confirmReset(token, password);
      form.reset(); setToken(null); setDone(true);
    } catch(err) {
      const status = (err as {response?: {status?: number}})?.response?.status;
      if ([400, 401, 404, 410].includes(status || 0)) {setInvalid(true); setToken(null);}
      else if (status === 422) setError('Use uma senha de 12 a 72 caracteres e confira os campos.');
      else setError(membersError(err));
    } finally {setBusy(false);}
  }
  return (
    <MembersShell>
      <Card className="mx-auto max-w-lg">
        <h1 className="text-2xl font-semibold">{done ? 'Senha atualizada' : 'Criar nova senha'}</h1>
        {!ready ? <Loading /> : done ? (
          <div className="mt-5 space-y-5"><CheckCircle2 className="members-link" size={30} aria-hidden="true" /><Notice>Sua senha foi alterada. Entre novamente para acessar sua área de parceiro.</Notice><Link className={primary} to="/members/login">Entrar na minha conta</Link></div>
        ) : invalid ? (
          <div className="mt-5 space-y-5"><Notice error>Este link é inválido, expirou ou já foi utilizado. Solicite um novo link para continuar.</Notice><Link className={primary} to="/members/forgot-password">Solicitar novo link</Link></div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4">
            <p className="members-secondary text-sm">Use pelo menos 12 caracteres para proteger sua conta.</p>
            <Field label="Nova senha" name="password" type="password" autoComplete="new-password" minLength={12} maxLength={72} disabled={busy} required />
            <Field label="Confirmar nova senha" name="confirmation" type="password" autoComplete="new-password" minLength={12} maxLength={72} disabled={busy} required />
            {error && <Notice error>{error}</Notice>}
            <button className={`${primary} w-full`} disabled={busy}>{busy ? 'Atualizando...' : 'Salvar nova senha'}</button>
          </form>
        )}
        {!done && <Link className="members-link mt-5 inline-flex min-h-[40px] items-center text-sm underline" to="/members/login">Voltar para o login</Link>}
        <MembersAsaasSeal />
      </Card>
    </MembersShell>
  );
}
